import { useCallback, useContext, useEffect } from 'react';
import styled from '@emotion/styled';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { StopCircle } from 'react-feather';
import {
  InitiateQuerySpeechToText,
  TextToSpeechStateContext,
  StopTextToSpeechContext,
  VoiceModeContext,
} from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context';
import { ChatResponseType } from '@trinity/chat';
import { BaseMessageButton } from './Message.Button';
import { ActivityIndicator } from '../presentation/ActivityIndicator';
import { useHasChatReadAloudEnabled } from '@trinity/chat-components/containers/Chat/selectors';

type Props = {
  kolSearchId?: number | null;
  queryId?: number | null;
  compact?: boolean;
};

export const ReadAloudButton = ({ kolSearchId, queryId, compact }: Props) => {
  const hasChatReadAloudEnabled = useHasChatReadAloudEnabled();

  const initiateReadAloud = useContext(InitiateQuerySpeechToText);
  const stopTextToSpeech = useContext(StopTextToSpeechContext);
  const audioState = useContext(TextToSpeechStateContext);
  const voicemode = useContext(VoiceModeContext);

  const isActive = audioState && (audioState.kolSearchId === kolSearchId || audioState.queryId === queryId);
  const isPlaying = audioState?.state === 'playing' && isActive;
  const isLoading = audioState?.state === 'loading' && isActive;
  const isError = audioState?.state === 'error' && isActive;

  const startPlaying = useCallback(async () => {
    if (isPlaying) {
      stopTextToSpeech?.();
      return;
    }

    return initiateReadAloud({
      kolSearchId,
      queryId,
    });
  }, [isPlaying, stopTextToSpeech, initiateReadAloud, kolSearchId, queryId]);

  const autoPlayReadAloud = useCallback(() => {
    if (voicemode.enabled && voicemode.type !== ChatResponseType.HcpSummary && voicemode.queryId === queryId) {
      if (audioState.state === 'playing') {
        stopTextToSpeech?.();
      }
      startPlaying();
      voicemode.dequeue();
    }
  }, [
    audioState,
    startPlaying,
    queryId,
    stopTextToSpeech,
    voicemode,
  ]);

  useEffect(() => {
    autoPlayReadAloud();
  }, []);

  if (!hasChatReadAloudEnabled) return null;

  if (isPlaying) {
    return (
      <StyledButton compact={compact} onClick={() => stopTextToSpeech()}>
        <FilledStopCircle size={18} />
        <TextLabel compact={compact}>Stop</TextLabel>
      </StyledButton>
    );
  }

  if (isLoading) {
    return (
      <StyledButton compact={compact}>
        <ActivityIndicator />
      </StyledButton>
    );
  }

  if (isError) {
    return (
      <StyledButton compact={compact}>
        <TextLabel compact={compact}>Error</TextLabel>
      </StyledButton>
    );
  }

  return (
    <StyledButton compact={compact} onClick={startPlaying}>
      <PlayArrowIcon fontSize="small" />
      <TextLabel compact={compact}>Read Aloud</TextLabel>
    </StyledButton>
  );
};

const StyledButton = styled(BaseMessageButton, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  display: 'flex',
  justifyContent: 'left',
  gap: '4px',
  ...(compact ? { minWidth: 'auto' } : { width: 115 }),
}));

const TextLabel = styled('span', {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  ...(compact && {
    display: 'inline-block',
    maxWidth: 0,
    overflow: 'hidden',
    opacity: 0,
    whiteSpace: 'nowrap' as const,
    transition: 'max-width 0.2s ease, opacity 0.2s ease',
    'button:hover &': {
      maxWidth: 100,
      opacity: 1,
    },
  }),
}));

const FilledStopCircle = styled(StopCircle)`
  circle {
    fill: none;
  }
  rect {
    fill: currentColor;
  }
`;
